/**
 * F1 Crop + F6 Flip — pure-canvas client handlers.
 *
 * Branches on `intent.id`:
 *   - F1 → crop UI (aspect ratio buttons + draggable rectangle + rotate)
 *   - F6 → flip UI (two buttons: horizontal / vertical)
 *
 * Both share `clientCanvasHandler` (matches DISPATCH_MATRIX `client_canvas`).
 */

import { registerClientHandler } from './registry.js'
import { loadImage } from '../utils/mockup-composite.js'

const STAGE_SIZE = 360
let _activeListeners = []
let _activeRoot = null

function track(target, event, fn) {
  target.addEventListener(event, fn)
  _activeListeners.push(() => target.removeEventListener(event, fn))
}

function cleanupListeners() {
  while (_activeListeners.length) {
    try { _activeListeners.pop()() } catch {}
  }
  _activeRoot = null
}

// ---- Public handler ----

export const clientCanvasHandler = {
  mountControls(rootEl, ctx) {
    cleanupListeners()
    _activeRoot = rootEl
    if (ctx.intent?.id === 'F6') return mountFlipUI(rootEl, ctx)
    return mountCropUI(rootEl, ctx)
  },

  unmount() {
    cleanupListeners()
    if (_activeRoot) _activeRoot.innerHTML = ''
  },

  async run(ctx) {
    if (ctx.intent?.id === 'F6') {
      const dataUrl = await flipImage(ctx.image, 'horizontal')
      return { result_data_url: dataUrl, metadata: { axis: 'horizontal' } }
    }
    // Default: 1:1 centered crop, no rotation
    const dataUrl = await cropDefault(ctx.image, '1:1')
    return { result_data_url: dataUrl, metadata: { aspect_ratio: '1:1' } }
  },
}

registerClientHandler('clientCanvasHandler', clientCanvasHandler)

// ---- F6 Flip ----

function mountFlipUI(rootEl, ctx) {
  rootEl.innerHTML = `
    <div class="ctt-flip">
      <p class="ctt-hint">Mirror your image:</p>
      <div class="ctt-button-row">
        <button type="button" class="ctt-btn" data-axis="horizontal">
          <i class="ph ph-flip-horizontal"></i> Horizontal
        </button>
        <button type="button" class="ctt-btn" data-axis="vertical">
          <i class="ph ph-flip-vertical"></i> Vertical
        </button>
      </div>
    </div>
  `

  rootEl.querySelectorAll('[data-axis]').forEach(btn => {
    track(btn, 'click', async () => {
      try {
        ctx.onProgress?.('Flipping...')
        const dataUrl = await flipImage(ctx.image, btn.dataset.axis)
        ctx.onResult?.({
          result_data_url: dataUrl,
          metadata: { axis: btn.dataset.axis },
        })
      } catch (err) {
        ctx.onError?.(err)
      }
    })
  })
}

async function flipImage(imageUrl, axis) {
  const img = await loadImage(imageUrl)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const c = canvas.getContext('2d')
  c.translate(
    axis === 'horizontal' ? canvas.width : 0,
    axis === 'vertical' ? canvas.height : 0,
  )
  c.scale(
    axis === 'horizontal' ? -1 : 1,
    axis === 'vertical' ? -1 : 1,
  )
  c.drawImage(img, 0, 0)
  return canvas.toDataURL('image/png')
}

// ---- F1 Crop ----

const ASPECT_PRESETS = {
  '1:1':  1,
  '4:5':  4 / 5,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  'free': null,
}

function mountCropUI(rootEl, ctx) {
  const allowed = (ctx.config?.aspect_ratios && Array.isArray(ctx.config.aspect_ratios))
    ? ctx.config.aspect_ratios
    : ['1:1', '4:5', '16:9', 'free']
  let activeRatio = allowed[0]
  let rotation = 0
  let crop = null
  let img = null
  let stageRect = { x: 0, y: 0, width: STAGE_SIZE, height: STAGE_SIZE }

  rootEl.innerHTML = `
    <div class="ctt-crop">
      <div class="ctt-controls">
        <label>Aspect:</label>
        ${allowed.map(r => `
          <button type="button" class="ctt-pill ${r === activeRatio ? 'active' : ''}" data-ratio="${r}">${r}</button>
        `).join('')}
        <span class="ctt-spacer"></span>
        <button type="button" class="ctt-pill" data-rotate="-90">↺ 90°</button>
        <button type="button" class="ctt-pill" data-rotate="90">↻ 90°</button>
        <button type="button" class="ctt-pill" data-rotate="180">↻ 180°</button>
      </div>
      <div class="ctt-stage" style="position:relative;width:${STAGE_SIZE}px;height:${STAGE_SIZE}px;background:var(--gray-50);overflow:hidden">
        <canvas class="ctt-stage-canvas" width="${STAGE_SIZE}" height="${STAGE_SIZE}" style="display:block"></canvas>
        <div class="ctt-crop-box" style="position:absolute;border:2px solid var(--gray-900);box-shadow:0 0 0 9999px var(--bg-overlay);pointer-events:none"></div>
      </div>
    </div>
  `

  const stageCanvas = rootEl.querySelector('.ctt-stage-canvas')
  const stageEl = rootEl.querySelector('.ctt-stage')
  const cropBoxEl = rootEl.querySelector('.ctt-crop-box')

  rootEl.querySelectorAll('[data-ratio]').forEach(btn => {
    track(btn, 'click', () => {
      activeRatio = btn.dataset.ratio
      rootEl.querySelectorAll('[data-ratio]').forEach(b => b.classList.toggle('active', b.dataset.ratio === activeRatio))
      crop = initialCrop(img, activeRatio)
      drawStage()
      emit()
    })
  })

  rootEl.querySelectorAll('[data-rotate]').forEach(btn => {
    track(btn, 'click', () => {
      rotation = (rotation + Number(btn.dataset.rotate) + 360) % 360
      crop = initialCrop(img, activeRatio)
      drawStage()
      emit()
    })
  })

  let dragState = null
  track(stageEl, 'pointerdown', (e) => {
    if (!crop) return
    stageEl.setPointerCapture?.(e.pointerId)
    dragState = { startX: e.offsetX, startY: e.offsetY, origCrop: { ...crop } }
  })
  track(stageEl, 'pointermove', (e) => {
    if (!dragState || !crop) return
    const dx = (e.offsetX - dragState.startX) * (img.naturalWidth / STAGE_SIZE)
    const dy = (e.offsetY - dragState.startY) * (img.naturalHeight / STAGE_SIZE)
    crop = clampCrop({
      x: dragState.origCrop.x + dx,
      y: dragState.origCrop.y + dy,
      width: dragState.origCrop.width,
      height: dragState.origCrop.height,
    }, img)
    drawStage()
    emitDebounced()
  })
  track(stageEl, 'pointerup', () => { dragState = null; emit() })
  track(stageEl, 'pointercancel', () => { dragState = null })

  loadImage(ctx.image).then(loaded => {
    img = loaded
    crop = initialCrop(img, activeRatio)
    drawStage()
    emit()
  }).catch(err => ctx.onError?.(err))

  function drawStage() {
    if (!img) return
    const c = stageCanvas.getContext('2d')
    // Preview-stage letterbox paint. Kept as a literal because canvas
    // fillStyle cannot consume CSS var(); mirrors --gray-50 (the stage bg
    // above). Never exported — exportCrop() draws on a separate canvas.
    c.fillStyle = '#111'
    c.fillRect(0, 0, STAGE_SIZE, STAGE_SIZE)
    const fit = fitInside(img.naturalWidth, img.naturalHeight, STAGE_SIZE, STAGE_SIZE)
    stageRect = fit
    if (rotation) {
      c.save()
      c.translate(STAGE_SIZE / 2, STAGE_SIZE / 2)
      c.rotate((rotation * Math.PI) / 180)
      c.drawImage(img, -fit.width / 2, -fit.height / 2, fit.width, fit.height)
      c.restore()
    } else {
      c.drawImage(img, fit.x, fit.y, fit.width, fit.height)
    }
    if (crop) drawCropBox()
  }

  function drawCropBox() {
    const sx = stageRect.width / img.naturalWidth
    const sy = stageRect.height / img.naturalHeight
    const left = stageRect.x + crop.x * sx
    const top = stageRect.y + crop.y * sy
    const w = crop.width * sx
    const h = crop.height * sy
    cropBoxEl.style.left = `${left}px`
    cropBoxEl.style.top = `${top}px`
    cropBoxEl.style.width = `${w}px`
    cropBoxEl.style.height = `${h}px`
  }

  let emitTimer = null
  function emitDebounced() {
    clearTimeout(emitTimer)
    emitTimer = setTimeout(emit, 60)
  }
  function emit() {
    if (!img || !crop) return
    const dataUrl = exportCrop(img, crop, rotation)
    ctx.onResult?.({
      result_data_url: dataUrl,
      metadata: { bbox: { ...crop }, rotation, aspect_ratio: activeRatio },
    })
  }
}

function initialCrop(img, ratio) {
  if (!img) return null
  const target = ASPECT_PRESETS[ratio]
  if (target == null) {
    return { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight }
  }
  const imgRatio = img.naturalWidth / img.naturalHeight
  let w, h
  if (imgRatio > target) {
    h = img.naturalHeight
    w = h * target
  } else {
    w = img.naturalWidth
    h = w / target
  }
  return {
    x: (img.naturalWidth - w) / 2,
    y: (img.naturalHeight - h) / 2,
    width: w,
    height: h,
  }
}

function clampCrop(c, img) {
  const x = Math.max(0, Math.min(c.x, img.naturalWidth - c.width))
  const y = Math.max(0, Math.min(c.y, img.naturalHeight - c.height))
  return { ...c, x, y }
}

function fitInside(w, h, maxW, maxH) {
  const scale = Math.min(maxW / w, maxH / h)
  const fw = w * scale
  const fh = h * scale
  return { x: (maxW - fw) / 2, y: (maxH - fh) / 2, width: fw, height: fh }
}

function exportCrop(img, crop, rotation) {
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(crop.width)
  canvas.height = Math.round(crop.height)
  const c = canvas.getContext('2d')
  if (rotation) {
    c.translate(canvas.width / 2, canvas.height / 2)
    c.rotate((rotation * Math.PI) / 180)
    c.drawImage(
      img,
      crop.x, crop.y, crop.width, crop.height,
      -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height,
    )
  } else {
    c.drawImage(
      img,
      crop.x, crop.y, crop.width, crop.height,
      0, 0, canvas.width, canvas.height,
    )
  }
  return canvas.toDataURL('image/png')
}

async function cropDefault(imageUrl, ratio) {
  const img = await loadImage(imageUrl)
  const crop = initialCrop(img, ratio)
  return exportCrop(img, crop, 0)
}
